(() => {
  'use strict';
  const dialog = document.getElementById('jaacks-ean-scanner');
  if (!dialog || typeof dialog.showModal !== 'function') return;
  const video = dialog.querySelector('video');
  const status = dialog.querySelector('[data-ean-status]');
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  let stream, timer, generation = 0, libraryPromise, returnFocus;
  function validEAN(value) {
    if (!/^(\d{8}|\d{13})$/.test(value)) return false;
    let sum = 0;
    for (let i = value.length - 2, weight = 3; i >= 0; i--, weight = 4 - weight) sum += Number(value[i]) * weight;
    return (10 - sum % 10) % 10 === Number(value[value.length - 1]);
  }
  function stop() {
    generation++;
    clearTimeout(timer);
    if (stream) stream.getTracks().forEach(track => track.stop());
    stream = null;
    video.srcObject = null;
  }
  async function search(code) {
    if (!validEAN(code)) return;
    stop();
    const session = generation;
    dialog.querySelector('[name=ean]').value = code;
    status.textContent = 'EAN ' + code + ' erkannt. Passende Größe wird gesucht …';
    const url = new URL(dialog.dataset.searchUrl, location.origin);
    url.searchParams.set('q', 'variants.barcode:' + code);
    url.searchParams.set('type', 'product');
    url.searchParams.set('view', 'jaacks-ean');
    url.searchParams.set('options[unavailable_products]', 'show');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url.href, { credentials:'same-origin', signal:controller.signal });
      if (!response.ok) throw new Error('lookup');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const payload = html.querySelector('#jaacks-ean-results');
      if (!payload) throw new Error('template');
      const data = JSON.parse(payload.textContent);
      if (!Array.isArray(data.matches)) throw new Error('payload');
      if (generation !== session || !dialog.open) return;
      const matches = data.matches.filter(item => item.barcode === code);
      if (matches.length === 0) {
        status.textContent = 'Für EAN ' + code + ' wurde keine Variante im Onlineshop gefunden. Bitte prüfe den Code und ob der Artikel im Onlineshop veröffentlicht ist.';
        return;
      }
      if (matches.length !== 1) {
        status.textContent = 'Diese EAN ist mehreren Varianten zugeordnet. Bitte suche den Artikel über seinen Namen.';
        return;
      }
      const target = new URL(matches[0].url, location.origin);
      if (target.origin !== location.origin || !target.pathname.includes('/products/') || !target.searchParams.has('variant')) throw new Error('target');
      if (preview) target.searchParams.set('preview_theme_id', preview);
      status.textContent = 'Artikel gefunden. Passende Größe wird geöffnet …';
      location.assign(target.href);
    } catch (_) {
      if (generation === session && dialog.open) status.textContent = 'Die Artikelsuche konnte nicht geladen werden. Bitte tippe auf Suchen, um es erneut zu versuchen.';
    } finally { clearTimeout(timeout); }
  }
  function loadLibrary() {
    if (window.ZXingBrowser) return Promise.resolve(window.ZXingBrowser);
    if (!libraryPromise) libraryPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = dialog.dataset.library;
      script.onload = () => window.ZXingBrowser ? resolve(window.ZXingBrowser) : reject(new Error('library'));
      script.onerror = () => { script.remove(); reject(new Error('library')); };
      document.head.appendChild(script);
    }).catch(error => { libraryPromise = null; throw error; });
    return libraryPromise;
  }
  async function start() {
    stop();
    const session = generation;
    status.textContent = 'Bitte erlaube den Kamerazugriff …';
    if (!navigator.mediaDevices?.getUserMedia || !window.isSecureContext) {
      status.textContent = 'Die Kamera ist hier nicht verfügbar. Öffne den Shop in Safari oder Chrome oder gib die EAN unten ein.';
      return;
    }
    try {
      const library = await loadLibrary();
      if (session !== generation || !dialog.open) return;
      const camera = await navigator.mediaDevices.getUserMedia({ audio:false, video:{ facingMode:{ ideal:'environment' }, width:{ ideal:1280 }, height:{ ideal:720 } } });
      if (session !== generation || !dialog.open) { camera.getTracks().forEach(track => track.stop()); return; }
      stream = camera;
      video.srcObject = stream;
      await video.play();
      if (session !== generation || !dialog.open) return;
      const reader = new library.BrowserMultiFormatOneDReader();
      let previous = '', matches = 0;
      const started = Date.now();
      status.textContent = 'Strichcode vollständig ins Kamerabild halten.';
      function scan() {
        if (session !== generation || !dialog.open) return;
        if (Date.now() - started > 45000) {
          stop();
          status.textContent = 'Noch kein Code erkannt. Versuche es mit mehr Licht und etwas Abstand erneut oder gib die EAN ein.';
          return;
        }
        if (video.readyState >= 2 && video.videoWidth) {
          canvas.width = Math.min(video.videoWidth, 1280);
          canvas.height = Math.round(video.videoHeight * canvas.width / video.videoWidth);
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          try {
            const code = reader.decodeFromCanvas(canvas).getText();
            if (validEAN(code)) {
              matches = code === previous ? matches + 1 : 1;
              previous = code;
              if (matches >= 2) { search(code); return; }
            }
          } catch (_) { /* A frame without a readable barcode is expected. */ }
        }
        timer = setTimeout(scan, 180);
      }
      scan();
    } catch (error) {
      if (session !== generation || !dialog.open) return;
      stop();
      status.textContent = error.name === 'NotAllowedError'
        ? 'Kamerazugriff nicht erlaubt. Erlaube ihn in den Browser-Einstellungen oder gib die EAN unten ein.'
        : 'Die Kamera konnte nicht gestartet werden. Versuche es erneut oder gib die EAN unten ein.';
    }
  }
  // Globo creates its mobile fullscreen search after the theme has loaded.
  function attachGloboScanner() {
    document.querySelectorAll('.gl-d-searchbox').forEach(box => {
      if (!box.querySelector('.gl-d-searchbox-input') || box.querySelector('[data-ean-open]')) return;
      const source = document.querySelector('.search-modal__form [data-ean-open]');
      if (!source) return;
      const button = source.cloneNode(true);
      button.className = 'jaacks-ean-globo-button';
      button.title = 'EAN-Code scannen';
      box.appendChild(button);
    });
  }
  let attachQueued = false;
  const observer = new MutationObserver(() => {
    if (attachQueued) return;
    attachQueued = true;
    requestAnimationFrame(() => { attachQueued = false; attachGloboScanner(); });
  });
  observer.observe(document.body, { childList:true, subtree:true });
  attachGloboScanner();
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-ean-open]');
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    const modal = trigger.closest('details-modal');
    returnFocus = modal?.querySelector('summary') || trigger;
    if (modal && typeof modal.close === 'function') modal.close(false);
    dialog.showModal();
    start();
  }, true);
  dialog.querySelector('[data-ean-close]').addEventListener('click', () => dialog.close());
  dialog.querySelector('[data-ean-retry]').addEventListener('click', start);
  dialog.addEventListener('close', () => { stop(); returnFocus?.focus(); });
  dialog.querySelector('[data-ean-manual]').addEventListener('submit', event => {
    event.preventDefault();
    const code = dialog.querySelector('[name=ean]').value.trim();
    if (validEAN(code)) search(code);
    else status.textContent = 'Bitte prüfe die EAN. Der Code muss 8 oder 13 Ziffern und eine gültige Prüfziffer enthalten.';
  });
  window.addEventListener('pagehide', stop);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden && dialog.open) { stop(); status.textContent = 'Scanner pausiert. Tippe auf „Erneut scannen“, um fortzufahren.'; }
  });
})();
